import React, { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { BankDocumentsRoutes } from '../../../Router/path';
import { BankDocumentEntryModel } from '../Model/bankDocument.model';
import {
  formatBankDocumentAllocationSummaryLabel,
  buildBankDocumentAllocationSummary,
} from '../Helpers/allocationSummary';
import { useBankDocumentDetails } from '../Hooks/useBankDocumentDetails';
import { useAuthUser } from '../../Auth/hooks/useAuthHooks';
import {
  useDeleteBankDocumentMutation,
  useReverseSupplierPrepaymentLinksMutation,
  useUpdateBankDocumentEntryMutation,
} from '../Service/bankDocumentsService';
import { buildUpdateBankDocumentEntryRequest } from '../Service/bankDocumentsRequestBuilder';
import AssignEntryDialog, { AssignEntryDialogResult } from '../Components/AssignEntryDialog';
import BankDocumentAllocationDetails, {
  BankDocumentAllocationCell,
  hasCollapsibleAllocations,
} from '../Components/BankDocumentAllocationDetails';
import { useGetBankDocumentCompaniesLookupQuery } from '../Service/companiesLookupApi';
import { useGetBankDocumentBankAccountsLookupQuery } from '../Service/bankAccountsLookupApi';
import {
  PostBankDocumentBatchRowResultDto,
  PostBankDocumentBatchResultDto,
} from '../Types/bankDocument.types';
import {
  getBankDocumentsErrorCode,
  getBankDocumentsErrorMessage,
} from '../Utils/bankDocumentsErrorMapping';

const BANK_DOCUMENT_DELETE_CONFLICT_SUPPLIER_PREPAYMENT_CODE =
  'BANK_DOCUMENT_DELETE_CONFLICT_SUPPLIER_PREPAYMENT';
const BANK_DOCUMENT_ENTRIES_SECTION_ID = 'bank-document-entries-section';
const BANK_DOCUMENT_ENTRY_COLUMNS: Array<{ label: string; align?: 'right' | 'center' }> = [
  { label: 'ID' },
  { label: 'Data księgowania' },
  { label: 'Data waluty' },
  { label: 'Kwota', align: 'right' },
  { label: 'Tytuł' },
  { label: 'Nadawca / Odbiorca' },
  { label: 'Przypisanie' },
  { label: 'Status' },
  { label: 'Diagnostyka' },
  { label: 'Metadane księgowania' },
  { label: 'Akcje', align: 'center' },
];

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleString('pl-PL');
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    const localDate = new Date(year, month - 1, day);

    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toLocaleDateString('pl-PL');
    }
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleDateString('pl-PL');
};

const formatAmount = (value: number, currency: string): string => {
  return `${value.toFixed(2)} ${currency}`;
};

const resolveAssignment = (entry: BankDocumentEntryModel): string => {
  const allocationSummary = buildBankDocumentAllocationSummary(entry);
  if (allocationSummary.allocationCount > 0) {
    return formatBankDocumentAllocationSummaryLabel(allocationSummary);
  }

  if (entry.invoiceId) {
    return `Faktura #${entry.invoiceId}`;
  }

  if (entry.reservationId) {
    return `Rezerwacja #${entry.reservationId}`;
  }

  if (entry.expenseInvoiceId) {
    return `FV kosztowa #${entry.expenseInvoiceId}`;
  }

  return '-';
};

const resolvePostingMetadata = (entry: BankDocumentEntryModel): string => {
  if (!entry.isPosted) {
    return '-';
  }

  return [
    entry.postedAtUtc ? `Data: ${formatDateTime(entry.postedAtUtc)}` : null,
    entry.postedByUserId ? `Użytkownik: ${entry.postedByUserId}` : null,
    entry.invoicePaymentId ? `Płatność: ${entry.invoicePaymentId}` : null,
    entry.reservationPaymentId ? `Płatność rezerwacji: ${entry.reservationPaymentId}` : null,
  ]
    .filter(Boolean)
    .join(' | ');
};

const renderAssignmentDetails = (
  entry: BankDocumentEntryModel,
  allocationsExpanded: boolean,
  onToggleAllocations: () => void
): React.ReactElement => {
  if (hasCollapsibleAllocations(entry)) {
    return (
      <BankDocumentAllocationCell
        entry={entry}
        expanded={allocationsExpanded}
        onToggle={onToggleAllocations}
      />
    );
  }

  const allocationSummary = buildBankDocumentAllocationSummary(entry);
  const allocationDetails =
    entry.allocations.length > 0
      ? entry.allocations.map((allocation) => ({
          key: String(allocation.id),
          label: `${allocation.targetType === 'invoice' ? 'Faktura' : 'Rezerwacja'} #${allocation.targetId}: ${allocation.allocatedAmount.toFixed(2)} ${entry.currency}`,
        }))
      : entry.invoiceId
        ? [{ key: `invoice-${entry.invoiceId}`, label: `Faktura #${entry.invoiceId}` }]
        : entry.reservationId
          ? [{ key: `reservation-${entry.reservationId}`, label: `Rezerwacja #${entry.reservationId}` }]
          : [];

  if (allocationSummary.allocationCount === 0) {
    return <>{resolveAssignment(entry)}</>;
  }

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{formatBankDocumentAllocationSummaryLabel(allocationSummary)}</Typography>
      <Typography variant="caption" color="text.secondary">
        Przypisano {allocationSummary.allocatedAmount.toFixed(2)} {entry.currency}, pozostało{' '}
        {allocationSummary.remainingAmount.toFixed(2)} {entry.currency}
      </Typography>
      {allocationDetails.map((detail) => (
        <Typography key={detail.key} variant="caption" color="text.secondary">
          {detail.label}
        </Typography>
      ))}
    </Stack>
  );
};

const resolveDiagnosticChipColor = (
  status?: PostBankDocumentBatchRowResultDto['status']
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'posted') {
    return 'success';
  }

  if (status === 'skipped') {
    return 'warning';
  }

  if (status === 'failed') {
    return 'error';
  }

  return 'default';
};

const resolveDiagnosticChipLabel = (
  status?: PostBankDocumentBatchRowResultDto['status']
): string => {
  if (status === 'posted') {
    return 'Gotowa / OK';
  }

  if (status === 'skipped') {
    return 'Pominięta';
  }

  if (status === 'failed') {
    return 'Błąd';
  }

  return 'Brak';
};

const buildDiagnosticsByEntryId = (
  diagnostics: PostBankDocumentBatchResultDto | null
): Record<number, PostBankDocumentBatchRowResultDto> => {
  if (!diagnostics) {
    return {};
  }

  return diagnostics.results.reduce<Record<number, PostBankDocumentBatchRowResultDto>>(
    (accumulator, row) => {
      accumulator[row.entryId] = row;
      return accumulator;
    },
    {}
  );
};

const resolveDiagnosticMessage = (diagnostic?: PostBankDocumentBatchRowResultDto): string => {
  if (!diagnostic?.message) {
    return '-';
  }

  return getBankDocumentsErrorMessage({ data: { errorCode: diagnostic.code } }, diagnostic.message);
};

interface RenderBankDocumentEntryRowArgs {
  entry: BankDocumentEntryModel;
  diagnostic?: PostBankDocumentBatchRowResultDto;
  focusedEntryId: number | null;
  isPosting: boolean;
  editSaving: boolean;
  allocationsExpanded: boolean;
  onToggleAllocations: (entryId: number) => void;
  onEditOpen: (entry: BankDocumentEntryModel) => void;
  onClearAssignment: (entry: BankDocumentEntryModel) => Promise<void>;
  onPostSingleEntry: (entryId: number) => Promise<void>;
  onUnpostSingleEntry: (entryId: number) => Promise<void>;
}

const renderBankDocumentEntryRow = ({
  entry,
  diagnostic,
  focusedEntryId,
  isPosting,
  editSaving,
  allocationsExpanded,
  onToggleAllocations,
  onEditOpen,
  onClearAssignment,
  onPostSingleEntry,
  onUnpostSingleEntry,
}: RenderBankDocumentEntryRowArgs): React.ReactElement => {
  const diagnosticMessage = resolveDiagnosticMessage(diagnostic);

  return (
    <TableRow
      key={entry.id}
      id={`bank-document-entry-row-${entry.id}`}
      hover
      selected={focusedEntryId === entry.id}
    >
      <TableCell>{entry.id}</TableCell>
      <TableCell>{formatDate(entry.bookingDate)}</TableCell>
      <TableCell>{formatDate(entry.valueDate)}</TableCell>
      <TableCell align="right">{formatAmount(entry.amount, entry.currency)}</TableCell>
      <TableCell>{entry.title || '-'}</TableCell>
      <TableCell>
        <div>{entry.counterpartyName || '-'}</div>
        {entry.counterpartyAccountNumber && (
          <div style={{ fontSize: '0.75rem', color: 'gray' }}>
            {entry.counterpartyAccountNumber}
          </div>
        )}
      </TableCell>
      <TableCell>
        {renderAssignmentDetails(entry, allocationsExpanded, () => onToggleAllocations(entry.id))}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={entry.isPosted ? 'success' : 'warning'}
          label={entry.isPosted ? 'Zaksięgowana' : 'Niezaksięgowana'}
        />
      </TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          <Chip
            size="small"
            color={resolveDiagnosticChipColor(diagnostic?.status)}
            label={resolveDiagnosticChipLabel(diagnostic?.status)}
          />
          {diagnostic?.code && (
            <Typography variant="caption" color="text.secondary">
              Kod: {diagnostic.code}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {diagnosticMessage}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>{resolvePostingMetadata(entry)}</TableCell>
      <TableCell align="center">
        <Stack direction="row" spacing={1} justifyContent="center">
          {!entry.isPosted && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEditOpen(entry)}
              disabled={isPosting}
            >
              Przypisz
            </Button>
          )}
          {!entry.isPosted && entry.hasPostingTarget && (
            <Button
              size="small"
              variant="text"
              color="warning"
              disabled={isPosting || editSaving}
              onClick={() => onClearAssignment(entry)}
            >
              Usuń przypisanie
            </Button>
          )}
          {!entry.hasPostingTarget && !entry.isPosted ? (
            <Button size="small" variant="outlined" disabled>
              Zaksięguj
            </Button>
          ) : (
            <Tooltip
              title={
                entry.isPosted
                  ? `Cofa księgowanie wyłącznie pozycji #${entry.id}.`
                  : 'Księguje tę pozycję.'
              }
            >
              <span>
                <Button
                  size="small"
                  variant={entry.isPosted ? 'outlined' : 'contained'}
                  color={entry.isPosted ? 'warning' : 'primary'}
                  startIcon={<CheckCircleIcon />}
                  disabled={entry.isPosted ? isPosting : isPosting || !entry.hasPostingTarget}
                  onClick={() =>
                    entry.isPosted ? onUnpostSingleEntry(entry.id) : onPostSingleEntry(entry.id)
                  }
                >
                  {entry.isPosted ? 'Cofnij księgowanie' : 'Zaksięguj'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

// Full-width row under an entry that holds the expandable allocation grid.
const renderBankDocumentAllocationsRow = (
  entry: BankDocumentEntryModel,
  expanded: boolean
): React.ReactElement | null => {
  if (!hasCollapsibleAllocations(entry)) {
    return null;
  }

  return (
    <TableRow>
      <TableCell
        colSpan={BANK_DOCUMENT_ENTRY_COLUMNS.length}
        sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}
      >
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <BankDocumentAllocationDetails entry={entry} />
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const BankDocumentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bankDocumentId } = useParams();
  const authUser = useAuthUser();

  // --- delete dialog state ---
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const [showDeleteSupplierPrepaymentHint, setShowDeleteSupplierPrepaymentHint] = useState(false);
  const [reverseSupplierPrepaymentSuccessMessage, setReverseSupplierPrepaymentSuccessMessage] =
    useState<string | null>(null);
  const [deleteBankDocument, { isLoading: isDeleting }] = useDeleteBankDocumentMutation();
  const [reverseSupplierPrepaymentLinks, { isLoading: isReversingSupplierPrepaymentLinks }] =
    useReverseSupplierPrepaymentLinksMutation();

  // --- assign entry dialog state ---
  const [editEntry, setEditEntry] = useState<BankDocumentEntryModel | null>(null);
  const [expandedAllocationEntryIds, setExpandedAllocationEntryIds] = useState<number[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editErrorMessage, setEditErrorMessage] = useState<string | null>(null);
  const [updateBankDocumentEntry] = useUpdateBankDocumentEntryMutation();
  const { companies } = useGetBankDocumentCompaniesLookupQuery();
  const {
    bankDocument,
    isInvalidId,
    isLoading,
    isError,
    isPosting,
    errorMessage,
    entryFilter,
    setEntryFilter,
    focusedEntryId,
    filteredEntries,
    postingDescription,
    setPostingDescription,
    postingSuccessMessage,
    postingErrorMessage,
    batchSuccessMessage,
    batchErrorMessage,
    isBatchProcessing,
    postingDiagnostics,
    focusEntry,
    handlePostSingleEntry,
    handleUnpostSingleEntry,
    handleQuickNextUnposted,
    handlePostBatch,
    handleRefreshPostingStatus,
  } = useBankDocumentDetails({ bankDocumentId, userId: authUser?.id ?? 0 });
  const { accounts } = useGetBankDocumentBankAccountsLookupQuery(
    bankDocument?.companyId ?? undefined
  );

  const parsedDocumentId = bankDocumentId ? parseInt(bankDocumentId, 10) : null;
  const selectedCompany = bankDocument?.companyId
    ? companies.find((company) => company.id === bankDocument.companyId)
    : null;
  const selectedAccount = bankDocument?.bankAccountId
    ? accounts.find((account) => account.id === bankDocument.bankAccountId)
    : null;
  const resolvedAccountNumber = bankDocument?.accountNumber ?? selectedAccount?.accountNumber;
  const latestPostedEntry = bankDocument?.entries
    ?.filter((entry) => Boolean(entry.postedAtUtc))
    .sort((left, right) => {
      const leftTs = left.postedAtUtc ? new Date(left.postedAtUtc).getTime() : 0;
      const rightTs = right.postedAtUtc ? new Date(right.postedAtUtc).getTime() : 0;
      return rightTs - leftTs;
    })[0];
  const diagnosticsByEntryId = buildDiagnosticsByEntryId(postingDiagnostics);

  useEffect(() => {
    if (!location.hash || !bankDocument) {
      return;
    }

    const match = /^#bank-document-entry-row-(\d+)$/.exec(location.hash);
    if (!match) {
      return;
    }

    const requestedEntryId = Number(match[1]);
    if (!Number.isInteger(requestedEntryId) || requestedEntryId <= 0) {
      return;
    }

    const entryExists = bankDocument.entries.some((entry) => entry.id === requestedEntryId);
    if (!entryExists) {
      return;
    }

    focusEntry(requestedEntryId);
  }, [location.hash, bankDocument, focusEntry]);

  const handleToggleAllocations = (entryId: number): void => {
    setExpandedAllocationEntryIds((current) =>
      current.includes(entryId) ? current.filter((id) => id !== entryId) : [...current, entryId]
    );
  };

  const handleEditOpen = (entry: BankDocumentEntryModel) => {
    setEditEntry(entry);
    setEditErrorMessage(null);
  };

  const handleEditClose = () => {
    setEditEntry(null);
    setEditErrorMessage(null);
  };

  const handleEditSave = async (result: AssignEntryDialogResult) => {
    if (!editEntry || !parsedDocumentId) return;

    setEditSaving(true);
    setEditErrorMessage(null);

    try {
      const request = buildUpdateBankDocumentEntryRequest(editEntry.id, parsedDocumentId, {
        title: editEntry.title,
        counterpartyName: editEntry.counterpartyName,
        counterpartyAccountNumber: editEntry.counterpartyAccountNumber,
        reference: editEntry.reference,
        invoiceId: result.invoiceId ?? null,
        reservationId: result.reservationId ?? null,
        expenseInvoiceId: result.expenseInvoiceId ?? null,
        allocations: result.allocations ?? null,
      });

      await updateBankDocumentEntry(request).unwrap();
      handleEditClose();
    } catch (error) {
      setEditErrorMessage(
        getBankDocumentsErrorMessage(error, 'Nie udało się zapisać przypisania. Spróbuj ponownie.')
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleClearAssignment = async (entry: BankDocumentEntryModel) => {
    if (!parsedDocumentId) {
      return;
    }

    setEditSaving(true);
    setEditErrorMessage(null);

    try {
      const request = buildUpdateBankDocumentEntryRequest(entry.id, parsedDocumentId, {
        title: entry.title,
        counterpartyName: entry.counterpartyName,
        counterpartyAccountNumber: entry.counterpartyAccountNumber,
        reference: entry.reference,
        invoiceId: null,
        reservationId: null,
        expenseInvoiceId: null,
      });

      await updateBankDocumentEntry(request).unwrap();
    } catch (error) {
      setEditErrorMessage(
        getBankDocumentsErrorMessage(error, 'Nie udało się usunąć przypisania. Spróbuj ponownie.')
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!parsedDocumentId) {
      return;
    }

    try {
      await deleteBankDocument(parsedDocumentId).unwrap();
      navigate(BankDocumentsRoutes.root);
    } catch (error) {
      const errorCode = getBankDocumentsErrorCode(error);
      const hasSupplierPrepaymentConflict =
        errorCode === BANK_DOCUMENT_DELETE_CONFLICT_SUPPLIER_PREPAYMENT_CODE;

      setShowDeleteSupplierPrepaymentHint(hasSupplierPrepaymentConflict);

      if (hasSupplierPrepaymentConflict) {
        setEntryFilter('all');
        window.setTimeout(() => {
          document
            ?.getElementById(BANK_DOCUMENT_ENTRIES_SECTION_ID)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      }

      setDeleteErrorMessage(
        getBankDocumentsErrorMessage(error, 'Nie udało się usunąć importu. Spróbuj ponownie.')
      );
      setDeleteDialogOpen(false);
    }
  };

  const handleReverseSupplierPrepaymentLinks = async (): Promise<void> => {
    if (!parsedDocumentId || !authUser?.id) {
      setDeleteErrorMessage('Brak użytkownika lub identyfikatora dokumentu do cofnięcia powiązań.');
      return;
    }

    setDeleteErrorMessage(null);
    setReverseSupplierPrepaymentSuccessMessage(null);

    try {
      const result = await reverseSupplierPrepaymentLinks({
        bankDocumentId: parsedDocumentId,
        createdByUserId: authUser.id,
      }).unwrap();

      setEntryFilter('all');
      setReverseSupplierPrepaymentSuccessMessage(
        `Cofnięto powiązania supplier prepayment dla ${result.reversedEntriesCount} pozycji.`
      );
      setShowDeleteSupplierPrepaymentHint(false);

      window.setTimeout(() => {
        document
          ?.getElementById(BANK_DOCUMENT_ENTRIES_SECTION_ID)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    } catch (error) {
      setDeleteErrorMessage(
        getBankDocumentsErrorMessage(
          error,
          'Nie udało się cofnąć powiązań supplier prepayment dla tego importu.'
        )
      );
    }
  };

  if (isInvalidId) {
    return <Alert severity="error">Nieprawidłowy identyfikator dokumentu bankowego.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(BankDocumentsRoutes.root)}
        >
          Powrót do listy
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={isLoading || isDeleting || !bankDocument}
          onClick={() => {
            setDeleteErrorMessage(null);
            setShowDeleteSupplierPrepaymentHint(false);
            setReverseSupplierPrepaymentSuccessMessage(null);
            setDeleteDialogOpen(true);
          }}
        >
          Usuń import
        </Button>
      </Box>

      {deleteErrorMessage && <Alert severity="error">{deleteErrorMessage}</Alert>}
      {reverseSupplierPrepaymentSuccessMessage && (
        <Alert severity="success">{reverseSupplierPrepaymentSuccessMessage}</Alert>
      )}
      {showDeleteSupplierPrepaymentHint && (
        <Alert
          severity="warning"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                disabled={isReversingSupplierPrepaymentLinks}
                onClick={handleReverseSupplierPrepaymentLinks}
              >
                {isReversingSupplierPrepaymentLinks
                  ? 'Cofanie powiązań...'
                  : 'Cofnij powiązania prepayment'}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setEntryFilter('all');
                  document
                    ?.getElementById(BANK_DOCUMENT_ENTRIES_SECTION_ID)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                Pokaż pozycje
              </Button>
            </Stack>
          }
        >
          Dokument ma aktywne powiązania supplier prepayment. Użyj „Cofnij powiązania prepayment”,
          następnie ponów usuwanie importu.
        </Alert>
      )}

      <AssignEntryDialog
        open={!!editEntry}
        entry={editEntry}
        saving={editSaving}
        errorMessage={editErrorMessage}
        companyId={bankDocument?.companyId}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Usuń import</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz usunąć cały import <strong>{bankDocument?.fileName}</strong>?
            Operacja jest nieodwracalna — zostaną usunięte wszystkie pozycje dokumentu.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Usuwanie…' : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomCard
        icon={<DescriptionIcon color="primary" fontSize="large" />}
        title="Szczegóły dokumentu bankowego"
        subheader={bankDocument ? `Plik: ${bankDocument.fileName}` : undefined}
      >
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity="error">{errorMessage}</Alert>}

        {!isLoading && !isError && bankDocument && (
          <Stack spacing={1}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Rachunek: <strong>{resolvedAccountNumber ?? '-'}</strong> &bull; Firma:{' '}
              <strong>{selectedCompany?.name ?? '-'}</strong>
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Tooltip title="Cofa globalnie ostatnio zaksięgowaną pozycję w tym imporcie (najnowszy znacznik postedAtUtc).">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<UndoIcon />}
                    disabled={isPosting || !latestPostedEntry}
                    onClick={() =>
                      latestPostedEntry && handleUnpostSingleEntry(latestPostedEntry.id)
                    }
                  >
                    Cofnij księgowanie
                  </Button>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {latestPostedEntry
                  ? `Globalne cofnięcie dotyczy pozycji #${latestPostedEntry.id} (ostatnia wg postedAtUtc).`
                  : 'Brak zaksięgowanych pozycji'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Przycisk globalny cofa tylko najnowszą pozycję wg postedAtUtc. Przycisk w wierszu
              cofa/księguje wyłącznie wskazaną pozycję.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {bankDocument.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Import: {formatDateTime(bankDocument.importedAtUtc)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Importujący (ID): {bankDocument.importedByUserId ?? '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Liczba pozycji: {bankDocument.entriesCount}
            </Typography>
          </Stack>
        )}
      </CustomCard>

      <CustomCard title="Pozycje dokumentu" icon={<DescriptionIcon color="primary" />}>
        <Box id={BANK_DOCUMENT_ENTRIES_SECTION_ID} />
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              size="small"
              fullWidth
              label="Opis księgowania (opcjonalne nadpisanie)"
              value={postingDescription}
              onChange={(event) => setPostingDescription(event.target.value)}
              disabled={isPosting}
            />
            <Button
              variant="outlined"
              startIcon={<SkipNextIcon />}
              onClick={handleQuickNextUnposted}
              disabled={isLoading || isError || isPosting}
            >
              Następna niezaksięgowana
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshPostingStatus}
              disabled={isLoading || isError || isPosting || isBatchProcessing || !bankDocument}
            >
              Odśwież status księgowania
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={handlePostBatch}
              disabled={
                isLoading ||
                isError ||
                isPosting ||
                isBatchProcessing ||
                !bankDocument ||
                !bankDocument.unpostedEntriesCount
              }
            >
              Zaksięguj batch
            </Button>
          </Stack>

          {postingSuccessMessage && <Alert severity="success">{postingSuccessMessage}</Alert>}
          {postingErrorMessage && <Alert severity="error">{postingErrorMessage}</Alert>}
          {batchSuccessMessage && <Alert severity="info">{batchSuccessMessage}</Alert>}
          {batchErrorMessage && <Alert severity="error">{batchErrorMessage}</Alert>}
          {editErrorMessage && <Alert severity="error">{editErrorMessage}</Alert>}

          {postingDiagnostics && (
            <Alert severity="info">
              Diagnostyka: posted <strong>{postingDiagnostics.postedCount}</strong>, skipped{' '}
              <strong>{postingDiagnostics.skippedCount}</strong>, failed{' '}
              <strong>{postingDiagnostics.failedCount}</strong> (snapshot:{' '}
              {formatDateTime(postingDiagnostics.processedAtUtc)}).
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Zaksięgowane: {bankDocument?.postedEntriesCount ?? 0} | Niezaksięgowane:{' '}
            {bankDocument?.unpostedEntriesCount ?? 0}
          </Typography>

          <ToggleButtonGroup
            value={entryFilter}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setEntryFilter(value);
              }
            }}
            size="small"
          >
            <ToggleButton value="all">Wszystkie</ToggleButton>
            <ToggleButton value="posted">Zaksięgowane</ToggleButton>
            <ToggleButton value="unposted">Niezaksięgowane</ToggleButton>
          </ToggleButtonGroup>

          {!isLoading && !isError && filteredEntries.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Brak pozycji spełniających wybrany filtr.
            </Typography>
          )}

          {!isLoading && !isError && filteredEntries.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {BANK_DOCUMENT_ENTRY_COLUMNS.map((column) => (
                      <TableCell key={column.label} align={column.align}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEntries.map((entry) => {
                    const allocationsExpanded = expandedAllocationEntryIds.includes(entry.id);

                    return (
                      <React.Fragment key={entry.id}>
                        {renderBankDocumentEntryRow({
                          entry,
                          diagnostic: diagnosticsByEntryId[entry.id],
                          focusedEntryId,
                          isPosting,
                          editSaving,
                          allocationsExpanded,
                          onToggleAllocations: handleToggleAllocations,
                          onEditOpen: handleEditOpen,
                          onClearAssignment: handleClearAssignment,
                          onPostSingleEntry: handlePostSingleEntry,
                          onUnpostSingleEntry: handleUnpostSingleEntry,
                        })}
                        {renderBankDocumentAllocationsRow(entry, allocationsExpanded)}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </CustomCard>
    </Stack>
  );
};

export default BankDocumentDetailsPage;
